/**
 * /legal/subprocessors — Sub-processors list.
 */

import { createFileRoute } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/legal/subprocessors')({
  component: SubprocessorsPage,
});

interface Subprocessor {
  name: string;
  purpose: string;
  location: string;
  data: string;
}

const SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'Microsoft Azure',
    purpose: 'Cloud compute, storage, Cosmos DB (trust records), Azure Blob (document vault), Azure Static Web Apps (frontend delivery)',
    location: 'South Africa North (primary); South Africa West (geo-redundant fallback)',
    data: 'All categories: trust records, FICA documents, audit trail, user accounts',
  },
  {
    name: 'Supabase',
    purpose: 'Authentication (email magic links, TOTP MFA management), row-level security session management',
    location: 'eu-west-1 Ireland (Supabase managed cloud)',
    data: 'Email address, authentication metadata, session tokens. No trust content data.',
  },
  {
    name: 'Azure Communication Services',
    purpose: 'Transactional email delivery (magic links, resolution notifications, distribution confirmations)',
    location: 'South Africa North',
    data: 'Email address, email subject, email body content for transactional notifications',
  },
  {
    name: 'ThisIsMe',
    purpose: 'Identity verification (FICA customer due diligence)',
    location: 'South Africa',
    data: 'Identity number, name, and identity-verification metadata for FICA checks',
  },
  {
    name: 'Paystack',
    purpose: 'Payment processing for subscription billing and trust distribution payouts',
    location: 'South Africa (Paystack Payments (Pty) Ltd)',
    data: 'Billing contact details and payout recipient banking details',
  },
  {
    name: 'Apple Push Notification service (APNs)',
    purpose: 'Push notification delivery to the Elreign iOS app',
    location: 'United States (Apple Inc.)',
    data: 'Device push token and notification payload metadata. No trust content data.',
  },
  {
    name: 'Google Firebase Cloud Messaging (FCM)',
    purpose: 'Push notification delivery to the Elreign Android app',
    location: 'United States (Google LLC)',
    data: 'Device push token and notification payload metadata. No trust content data.',
  },
  {
    name: 'Expo (EAS)',
    purpose: 'Mobile push-token relay and mobile app build service',
    location: 'United States (Expo, Inc. / 650 Industries, Inc.)',
    data: 'Device push token for notification routing. No trust content data.',
  },
];

function SubprocessorsPage() {
  return (
    <LegalLayout heading="Sub-processors." lastUpdated="7 May 2026">
      <p>
        Elreign uses a small number of sub-processors to deliver the Service. This page lists
        every third-party service that processes personal information on our behalf. We have
        contractual data processing agreements in place with each.
      </p>
      <p>
        We will update this page before onboarding any new sub-processor. If you have questions
        about any of these arrangements, contact us at{' '}
        <a href="mailto:privacy@elreign.io" style={{ color: 'var(--primary-600)' }}>
          privacy@elreign.io
        </a>
        .
      </p>

      <h2>Current sub-processors</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Sub-processor</th>
            <th>Purpose</th>
            <th>Location</th>
            <th>Data processed</th>
          </tr>
        </thead>
        <tbody>
          {SUBPROCESSORS.map((sp) => (
            <tr key={sp.name}>
              <td>
                <strong style={{ fontSize: 15 }}>{sp.name}</strong>
              </td>
              <td>{sp.purpose}</td>
              <td>{sp.location}</td>
              <td>{sp.data}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Transfer safeguards</h2>
      <p>
        Where personal information is processed outside South Africa by an EU processor (currently:
        Supabase in Ireland), this is permitted under section 72 of POPIA because the destination
        country (Ireland, an EU member state) provides an adequate level of protection equivalent to
        South African POPIA requirements by virtue of its compliance with GDPR. All transfers to EU
        processors are further governed by standard contractual clauses and Supabase&apos;s own DPA.
      </p>
      <p>
        The mobile push-notification processors (Apple Push Notification service, Google Firebase
        Cloud Messaging, and Expo) are located in the United States. These cross-border transfers are
        permitted under section 72 of POPIA on the basis that (a) each processor is bound by a data
        processing agreement imposing protections substantially similar to POPIA, and (b) the
        transfer is necessary for the performance of the Service you have requested, namely
        delivering push notifications to your device. Only an opaque device push token and
        notification metadata are transferred; no trust content, FICA documents, or beneficial-
        ownership data leave South Africa.
      </p>

      <h2>Notification of changes</h2>
      <p>
        We will update this page at least 14 days before adding a new sub-processor. If you
        object to a new sub-processor, you may raise your objection to our Information Officer.
        If the objection cannot be resolved, you may terminate the Service on 30 days written
        notice under the Terms of Service.
      </p>
    </LegalLayout>
  );
}
